import { useEffect } from "react";

/** The editors holding an unsaved draft: module-level, since what leaves cannot see into them. */
const unsaved = new Set<object>();

/**
 * The browser's question on the way out. `returnValue` too, on purpose: chrome111 shows no
 * dialog for preventDefault alone. Reasoning and when to delete it: eslint.config.js.
 */
function ask(event: BeforeUnloadEvent) {
  event.preventDefault();
  event.returnValue = "You have an unsaved draft.";
}

/** Count one thing as unsaved (a draft, or a write still out) until the release is called. */
export function holdUnsaved(): () => void {
  const held = {};
  unsaved.add(held);
  window.addEventListener("beforeunload", ask);
  return () => {
    unsaved.delete(held);
    if (unsaved.size === 0) window.removeEventListener("beforeunload", ask);
  };
}

/**
 * Ask the browser before it leaves the page with an unsaved draft. One rule for
 * every editor on the page; a draft that still reads as stored asks nothing.
 * While dirty the editor also counts in `hasUnsavedDraft`.
 */
export function useUnsavedDraft(dirty: boolean): void {
  useEffect(() => {
    if (!dirty) return;
    return holdUnsaved();
  }, [dirty]);
}

/** Whether any editor on the page holds an unsaved draft: what "Leave without saving?" asks about. */
export function hasUnsavedDraft(): boolean {
  return unsaved.size > 0;
}
